/**
 * Where a mission's clouds are, how many, and how big — all of it recovered.
 * No three.js, so it is plain unit tests.
 *
 * ## The original has a real cloud system
 *
 * Recovered 2026-08-23 by static RE of `RB.EXE` and `RBMB.EXE` (full evidence
 * trail in `docs/engineering/enhanced-sky.md` §1; method is
 * `sun-blind-spot-design.md`'s assert-relocation scan). `s\game\clouds.c` is a
 * ~416-line module at file `0xE290`, segment `0x9a9`:
 *
 * - `allocCloudList(n)` mallocs `n << 5` — a **32-byte cloud record**, position
 *   `LVEC` at `+8`.
 * - `addCloud(sizeClass, pos)` registers **`MISC.TBL` object `10 + sizeClass`**
 *   (`ds:0x7a7a` is `modelIdBase[1]`, and `MISC` is the second of the five
 *   hard-coded `.TBL` loads). There are three size classes, and a filter at
 *   `0xe60d` confirms the range as the inclusive `[base+10, base+12]`.
 * - A point-in-cloud test compares against a min/max box per class.
 * - A per-frame pass writes each object's cloud index to `+0x106` and its
 *   in-cloud state to `+0x107`; a later filter drops state-2 objects from a
 *   draw/target list. That is the manual's "clouds may be hiding enemy
 *   aircraft", per object per frame.
 * - The whole system is gated on REALISTIC WEATHER (`ds:0x726b`) — the manual:
 *   "with it turned off, there will be no clouds in the sky".
 *
 * So a cloud is **the original's own geometry**, not art this port invented:
 * `sky-clouds.ts` draws the baked `MISC-10`/`11`/`12`.
 *
 * ## The counts and the placement are the Mission Builder's own
 *
 * `RB.EXE` reads its cloud list out of the mission file (token 11, a 13-byte
 * record: `int8 sizeClass` then three `int32`), so nothing in the sim decides
 * how many clouds a sky word gets. `RBMB.EXE`'s `_BUILD_CLOUD_LIST` does, off a
 * 9-byte-per-word table at `ds:0x1154`, and that table is `COVER` below —
 * counts, size-class weights and minimum separation, verbatim. This port's
 * generators write no cloud list, so they run the Mission Builder's own
 * algorithm at load time instead.
 *
 * The shape of that design is worth stating because it is not what anyone would
 * guess: **`Overcast` generates zero clouds.** It is a *sky repaint*, not a
 * cloud layer (`overcast-sky.ts`). `Clear` is not empty — it can put up four,
 * half of them the largest class. `Cloudy` gets nine or ten and every one of
 * them is the 33,750-foot monster.
 */

import { fnv1a32, type SkyCondition } from "@red-opus/engine";

/** `clouds.c`'s position unit: an eighth of a foot. */
export const CLOUD_UNIT_M = 0.125 * 0.3048;

/** One size class's occlusion box, in metres, relative to the cloud's own position. */
export interface CloudBox {
  readonly minX: number;
  readonly minZ: number;
  /** The cloud's base: `clouds.c`'s vertical extent runs `0` to `+8000` units, so a position is a floor. */
  readonly minY: number;
  readonly maxX: number;
  readonly maxZ: number;
  readonly maxY: number;
}

function boxFromUnits(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): CloudBox {
  // The source frame is Z-up (docs/formats/models-tbl.md §4.2), so the file's
  // `z` is height and its `y` is the world's `-z`.
  return {
    minX: x0 * CLOUD_UNIT_M,
    maxX: x1 * CLOUD_UNIT_M,
    minZ: -y1 * CLOUD_UNIT_M,
    maxZ: -y0 * CLOUD_UNIT_M,
    minY: z0 * CLOUD_UNIT_M,
    maxY: z1 * CLOUD_UNIT_M,
  };
}

/**
 * `ds:0x077a` / `ds:0x0780`'s three boxes, verbatim. Class 0 is 33,750 x 30,000
 * feet across and **1,000 feet thick**; each next class is exactly half of the
 * one before on every axis, which is a strong check that the table was read
 * correctly rather than a coincidence.
 *
 * **These are the sim's occlusion volumes, not the drawn silhouette.** They come
 * out about 2.5x the baked `MISC-10`/`11`/`12` geometry, which is exactly the
 * ratio between `world-terrain.md`'s world unit (1/8 ft) and `models-tbl.md`'s
 * model unit (1/20 ft) — so one of the two conventions is off for this object
 * class, and which one is an open question (`enhanced-sky.md` §8). The renderer
 * uses the model; nothing yet uses these boxes. They are kept because the
 * in-cloud contact suppression will need them, and because
 * `enhanced-sky.md` should not have to re-derive them.
 */
export const CLOUD_SIZE_CLASSES: readonly CloudBox[] = [
  boxFromUnits(-110000, -105000, 0, 160000, 135000, 8000),
  boxFromUnits(-55000, -52500, 0, 80000, 67500, 4000),
  boxFromUnits(-27500, -26250, 0, 40000, 33750, 2000),
];

/** `clouds.c` `0xe44f`: past this range an object is not tested against any cloud at all. */
export const CLOUD_OCCLUSION_RANGE_M = 200000 * CLOUD_UNIT_M;

/** One placed cloud: which class's model, and where its base sits. */
export interface PlacedCloud {
  readonly sizeClass: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** `RBMB.EXE`'s `ds:0x1154` table — 9 bytes per sky word, in the Mission Builder's own order. */
interface CloudCover {
  readonly minCount: number;
  readonly maxCount: number;
  /** `_RAND_PROB` weights over the three size classes. */
  readonly classWeights: readonly [number, number, number];
  /** Minimum centre-to-centre separation, in `clouds.c`'s world units. */
  readonly separationUnits: number;
}

export const COVER: Readonly<Record<SkyCondition, CloudCover>> = {
  clear: { minCount: 0, maxCount: 4, classWeights: [50, 50, 0], separationUnits: 250000 },
  "partly cloudy": { minCount: 4, maxCount: 5, classWeights: [70, 30, 0], separationUnits: 220000 },
  cloudy: { minCount: 9, maxCount: 10, classWeights: [100, 0, 0], separationUnits: 220000 },
  // Weights present in the table but unreachable: the count is zero either way.
  overcast: { minCount: 0, maxCount: 0, classWeights: [40, 30, 30], separationUnits: 250000 },
};

/**
 * `_BUILD_CLOUD_LIST` draws `X, Y = 100 * RAND_RANGE(min_cloud_pos,
 * max_cloud_pos)` with those globals at **-6500** and **+6500**, so a cloud
 * lands within 650,000 world units — 81,250 feet, about 15.4 miles — of the
 * mission origin.
 */
export const CLOUD_SCATTER_HALF_EXTENT_M = 650000 * CLOUD_UNIT_M;

/**
 * Every cloud in a mission sits at **one** altitude: `_BUILD_CLOUD_LIST`
 * computes `Z = 5 * _CEILING` once, outside its placement loop, from the
 * Mission Builder's "Cloud altitude" field (`_CEILING`, initialised to
 * **10,000**).
 *
 * The `x5` does not reconcile with the 8-units-per-foot world unit that every
 * other position in `clouds.c` uses, and `_WRITE_CLOUD_LIST` was not
 * disassembled far enough to say whether it rescales. The field is labelled in
 * feet and the author types feet into it, so **the field's own value is what is
 * used here** — 10,000 ft — and the discrepancy is recorded rather than
 * silently resolved (`enhanced-sky.md` §8).
 */
export const CLOUD_CEILING_FT = 10000;
export const CLOUD_CEILING_M = CLOUD_CEILING_FT * 0.3048;

/**
 * The band this port draws a mission's ceiling from, in feet.
 *
 * PORT CHOICE, and the one place the cloud layer deliberately does not just run
 * the Mission Builder's algorithm. `_CEILING` is a **field the author fills in**
 * per mission — the Builder's "Cloud altitude" box, initialised to 10,000 — and
 * this port's generators write no cloud list at all, so there is no authored
 * value to read. Pinning every sortie to the initialiser puts every cloud in the
 * game at 10,000 ft, which is above where most of the flying happens: the
 * aircraft in `docs/game/aircraft.md` cruise well below it, and a cloudy sortie
 * then reads as a clear one with something grey a long way up.
 *
 * Drawing the ceiling from the mission seed instead is the closer analogue of
 * what the original does, because the original's answer *varies per mission*.
 * The band brackets the Builder's own default from below rather than around it:
 * 3,000 ft is a working cloud base and 10,000 ft is the value the Builder starts
 * on.
 */
export const CLOUD_CEILING_BAND_FT: readonly [number, number] = [3000, 10000];

/**
 * A 32-bit xorshift over the mission seed. Local rather than
 * `packages/engine`'s `RngBank` because the cloud field is **cosmetic** and
 * must never pull from a stream the sim also draws from — a renderer that can
 * advance the mission's randomness is a renderer that can change the fight.
 */
function scatterStream(seed: number): () => number {
  let state = fnv1a32("enhanced-sky/clouds", seed >>> 0) || 1;
  return () => {
    state ^= state << 13;
    state >>>= 0;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    return state / 0x1_0000_0000;
  };
}

/** `_RAND_PROB` over three weights — which size class this cloud is. */
function drawSizeClass(weights: readonly [number, number, number], roll: number): number {
  const total = weights[0] + weights[1] + weights[2];
  if (total <= 0) return 0;
  let cursor = roll * total;
  for (let index = 0; index < weights.length; index++) {
    cursor -= weights[index] ?? 0;
    if (cursor < 0) return index;
  }
  return weights.length - 1;
}

/**
 * How many redraws a cloud gets before its separation constraint is abandoned.
 *
 * `_BUILD_CLOUD_LIST` loops forever (`jmp b419`), which is fine when the author
 * is sitting in front of a dialog and not fine in a render path — ten clouds
 * needing 8.4 km of separation inside a 24.8 km square is a tight enough
 * packing that an unlucky seed could spin. Giving up after the cap places the
 * cloud anyway, which is strictly better than a hung sortie.
 */
const SEPARATION_ATTEMPTS = 64;

/**
 * The mission's clouds — `_BUILD_CLOUD_LIST`, with a bounded retry. Deterministic
 * in `seed`, so the same sortie flown twice has the same sky.
 */
export function placeClouds(sky: SkyCondition, seed: number): readonly PlacedCloud[] {
  const cover = COVER[sky];
  const draw = scatterStream(seed);
  const count = cover.minCount + Math.floor(draw() * (cover.maxCount - cover.minCount + 1));
  const separationM = cover.separationUnits * CLOUD_UNIT_M;
  // One altitude for the whole layer, drawn once outside the loop — which is
  // `_BUILD_CLOUD_LIST`'s own structure, with `CLOUD_CEILING_BAND_FT` standing in
  // for the field this port has no author to fill.
  const [lowFt, highFt] = CLOUD_CEILING_BAND_FT;
  const ceilingM = (lowFt + draw() * (highFt - lowFt)) * 0.3048;
  const placed: PlacedCloud[] = [];

  for (let index = 0; index < count; index++) {
    const sizeClass = drawSizeClass(cover.classWeights, draw());
    let x = 0;
    let z = 0;
    for (let attempt = 0; attempt < SEPARATION_ATTEMPTS; attempt++) {
      x = (draw() * 2 - 1) * CLOUD_SCATTER_HALF_EXTENT_M;
      z = (draw() * 2 - 1) * CLOUD_SCATTER_HALF_EXTENT_M;
      if (placed.every((other) => Math.hypot(other.x - x, other.z - z) > separationM)) break;
    }
    placed.push({ sizeClass, x, y: ceilingM, z });
  }
  return placed;
}

/** The box of a placed cloud in world metres — what an occlusion test would read. */
export function cloudBoxOf(cloud: PlacedCloud): CloudBox {
  const box = CLOUD_SIZE_CLASSES[cloud.sizeClass] ?? CLOUD_SIZE_CLASSES[CLOUD_SIZE_CLASSES.length - 1];
  if (!box) throw new Error("cloud-field: no size classes");
  return {
    minX: cloud.x + box.minX,
    maxX: cloud.x + box.maxX,
    minY: cloud.y + box.minY,
    maxY: cloud.y + box.maxY,
    minZ: cloud.z + box.minZ,
    maxZ: cloud.z + box.maxZ,
  };
}
